import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../lib/cn';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  message: React.ReactNode;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  toast: (message: React.ReactNode, type?: ToastType, duration?: number) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastType, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
};

const TONE: Record<ToastType, { accent: string; iconClass: string }> = {
  success: { accent: 'border-l-success', iconClass: 'text-success' },
  error: { accent: 'border-l-destructive', iconClass: 'text-destructive' },
  info: { accent: 'border-l-info', iconClass: 'text-info' },
  warning: { accent: 'border-l-warning', iconClass: 'text-warning' },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const idRef = React.useRef(0);

  const dismiss = React.useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = React.useCallback<ToastContextValue['toast']>((message, type = 'info', duration = 5000) => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, message, type, duration }]);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  const value = React.useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[9999] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:right-6 sm:left-auto sm:items-end">
            {toasts.map((t) => {
              const Icon = ICONS[t.type];
              const tone = TONE[t.type];
              return (
                <div
                  key={t.id}
                  role="status"
                  className={cn(
                    'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border border-border border-l-4 bg-popover p-3 pr-2 text-popover-foreground shadow-elevation-3 animate-fade-in-up',
                    tone.accent
                  )}
                >
                  <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', tone.iconClass)} aria-hidden="true" />
                  <div className="flex-1 text-sm">{t.message}</div>
                  <button
                    type="button"
                    onClick={() => dismiss(t.id)}
                    className="ml-2 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label="Dismiss"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
